import { createTheme } from '@mui/material/styles'

// ── Ardent Scholar design tokens ─────────────────────────────────────────────
//
// Palette rationale:
//   Primary  — Deep navy (#0F2B4C): academic, serious, trusted
//   Secondary — Warm gold (#C8973A): achievement, prestige, warmth
//   Surface  — Off-white (#F9F7F4): warm, not clinical, differentiates from grey
//   Text     — Near-black (#1A1A2E): softer than pure black, easier to read
//
// Typography:
//   Headings — Plus Jakarta Sans: modern, confident, distinctive
//   Body     — Plus Jakarta Sans: consistent family, weight does the work
//
// Shape:
//   8px border-radius throughout: modern but not playful
//
// Shadows:
//   Softer than MUI defaults — elevation feels like paper, not plastic

const NAVY  = '#0F2B4C'
const GOLD  = '#C8973A'
const SURFACE = '#F9F7F4'

const theme = createTheme({
  palette: {
    primary: {
      main:         NAVY,
      light:        '#1E4A80',
      dark:         '#081B30',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         GOLD,
      light:        '#D9AD5E',
      dark:         '#A07828',
      contrastText: '#FFFFFF',
    },
    background: {
      default: SURFACE,
      paper:   '#FFFFFF',
    },
    text: {
      primary:   '#1A1A2E',
      secondary: '#5A6A7A',
    },
    divider: 'rgba(15, 43, 76, 0.1)',
    grey: {
      50:  '#F9F7F4',
      100: '#F2EFE9',
      200: '#E5E0D8',
      300: '#CFC9BE',
      400: '#B0A898',
      500: '#8F8578',
    },
  },

  typography: {
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.015em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700, letterSpacing: '-0.005em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600, letterSpacing: '0' },
    subtitle2: { fontWeight: 600, letterSpacing: '0' },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' },
    caption: { letterSpacing: '0.01em' },
  },

  shape: {
    borderRadius: 8,
  },

  shadows: [
    'none',
    '0 1px 3px rgba(15,43,76,0.06), 0 1px 2px rgba(15,43,76,0.04)',
    '0 2px 6px rgba(15,43,76,0.08), 0 1px 3px rgba(15,43,76,0.05)',
    '0 4px 12px rgba(15,43,76,0.09), 0 2px 4px rgba(15,43,76,0.05)',
    '0 6px 16px rgba(15,43,76,0.10), 0 2px 6px rgba(15,43,76,0.06)',
    '0 8px 20px rgba(15,43,76,0.11)',
    '0 10px 24px rgba(15,43,76,0.12)',
    '0 12px 28px rgba(15,43,76,0.12)',
    '0 14px 32px rgba(15,43,76,0.13)',
    '0 16px 36px rgba(15,43,76,0.13)',
    '0 18px 40px rgba(15,43,76,0.14)',
    '0 20px 44px rgba(15,43,76,0.14)',
    '0 22px 48px rgba(15,43,76,0.15)',
    '0 24px 52px rgba(15,43,76,0.15)',
    '0 26px 56px rgba(15,43,76,0.16)',
    '0 28px 60px rgba(15,43,76,0.16)',
    '0 30px 64px rgba(15,43,76,0.17)',
    '0 32px 68px rgba(15,43,76,0.17)',
    '0 34px 72px rgba(15,43,76,0.18)',
    '0 36px 76px rgba(15,43,76,0.18)',
    '0 38px 80px rgba(15,43,76,0.19)',
    '0 40px 84px rgba(15,43,76,0.19)',
    '0 42px 88px rgba(15,43,76,0.20)',
    '0 44px 92px rgba(15,43,76,0.20)',
    '0 46px 96px rgba(15,43,76,0.21)',
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: SURFACE,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingTop:    8,
          paddingBottom: 8,
          paddingLeft:   20,
          paddingRight:  20,
        },
        contained: {
          '&:hover': { backgroundColor: '#1E4A80' },
        },
        outlined: {
          borderColor: 'rgba(15,43,76,0.3)',
          '&:hover': { borderColor: NAVY, backgroundColor: 'rgba(15,43,76,0.04)' },
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid rgba(15,43,76,0.1)',
          boxShadow: '0 1px 3px rgba(15,43,76,0.06)',
          borderRadius: 10,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 10 },
        elevation1: { boxShadow: '0 2px 6px rgba(15,43,76,0.08), 0 1px 3px rgba(15,43,76,0.05)' },
        elevation2: { boxShadow: '0 4px 12px rgba(15,43,76,0.09), 0 2px 4px rgba(15,43,76,0.05)' },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: NAVY,
          color: '#FFFFFF',
          borderRight: 'none',
          boxShadow: '2px 0 12px rgba(15,43,76,0.15)',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: 'rgba(255,255,255,0.75)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
            color: '#FFFFFF',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(200,151,58,0.2)',
            color: '#FFFFFF',
            '&:hover': { backgroundColor: 'rgba(200,151,58,0.28)' },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: { color: 'inherit', minWidth: 36 },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: '0',
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundColor: NAVY },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: 'rgba(15,43,76,0.1)' },
        bar:  { borderRadius: 4 },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(15,43,76,0.08)' },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderColor: 'rgba(15,43,76,0.2)',
          '&.Mui-selected': {
            backgroundColor: NAVY,
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#1E4A80' },
          },
        },
      },
    },
  },
})

export default theme
